// httpPorts — the Resource BC's Material-Content editing facet: the nine
// content-mutation primitives sharing the optimistic-lock `contentHash`
// round-trip (path + expectedContentHash → new hash).

import { Result } from "@bilda/utils";
import type { Choice, Element } from "../../components/Editor/types.js";
import { mkMalformed } from "../externalError.js";
import { toWireChoice, toWireElement } from "../materialAdapter.js";
import type { MaterialEditingPorts } from "../ports/materialEditing.js";
import type { PortResult } from "../ports/result.js";
import type { MaterialId } from "../ports.js";
import { fields, str } from "../wireShape.js";
import { command } from "./shared.js";

// One manual inline text edit through the SAME primitive the agent uses:
// `POST /commands/updateElement` with a `set_element_text` patch. The server
// guards the write with `expectedContentHash` and returns the freshly-computed
// `contentHash` on the ok envelope — read it back for the next edit. A stale
// hash (or any refusal) arrives as an `ok:false` envelope, which `httpClient`
// already maps to a typed `ExternalError` (Rejected); the host rolls back on it.

const updateElementText = async (
    materialId: MaterialId,
    path: readonly string[],
    text: string,
    expectedContentHash: string,
    baseText?: string,
): PortResult<{ readonly contentHash: string }> => {
    const r = await command("updateElement", {
        materialId,
        path: [...path],
        patch: { type: "set_element_text", text },
        expectedContentHash,
        ...(baseText === undefined ? {} : { baseText }),
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("updateElement returned no contentHash"));
    }
    return Result.ok({ contentHash });
};

// One manual element-attrs edit through the SAME primitive the agent uses:
// `POST /commands/updateElement` with a `set_element_attrs` patch. Used for
// per-choice MCQ feedback. The patch REPLACES the element's attrs (domain
// contract), so the caller passes the complete attrs record. Same optimistic-
// lock + new-hash round-trip as `updateElementText`.

const updateElementAttrs = async (
    materialId: MaterialId,
    path: readonly string[],
    attrs: Readonly<Record<string, unknown>>,
    expectedContentHash: string,
): PortResult<{ readonly contentHash: string }> => {
    const r = await command("updateElement", {
        materialId,
        path: [...path],
        patch: { type: "set_element_attrs", attrs },
        expectedContentHash,
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("updateElement returned no contentHash"));
    }
    return Result.ok({ contentHash });
};

// One manual element-attrs edit through the `patch_element_attrs` primitive:
// `POST /commands/updateElement` with a keyed-MERGE patch. Unlike
// `updateElementAttrs` (a full attrs replace), the given keys overlay the
// element's existing attrs, leaving the rest intact — so setting a choice's
// `correct` preserves its `feedback`. Same optimistic-lock + new-hash round-trip.

const patchElementAttrs = async (
    materialId: MaterialId,
    path: readonly string[],
    attrs: Readonly<Record<string, unknown>>,
    expectedContentHash: string,
): PortResult<{ readonly contentHash: string }> => {
    const r = await command("updateElement", {
        materialId,
        path: [...path],
        patch: { type: "patch_element_attrs", attrs },
        expectedContentHash,
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("updateElement returned no contentHash"));
    }
    return Result.ok({ contentHash });
};

// Append one block through the SAME primitive the agent uses:
// `POST /commands/insertChildElement` with `parentPath: []` (the document root)
// and `position: "last"`. The palette-minted UI element is serialised to the
// domain tree node by `toWireElement`; the server brands its ids at ingress.
// On success returns the new `contentHash` plus the block's domain path — always
// `[block.id]` at the root, so the host threads it into `pathById` and the block
// is immediately text-editable. A stale hash / id collision arrives as a typed
// `ExternalError` (the `ok:false` envelope), on which the host rolls back.

const insertBlockAt =
    (position: "first" | "last") =>
    async (
        materialId: MaterialId,
        block: Element,
        expectedContentHash: string,
    ): PortResult<{
        readonly contentHash: string;
        readonly path: readonly string[];
    }> => {
        const r = await command("insertChildElement", {
            materialId,
            parentPath: [],
            position,
            element: toWireElement(block),
            expectedContentHash,
        });
        if (Result.isErr(r)) return r;
        const contentHash = str(fields(r.value)?.get("contentHash"));
        if (contentHash === undefined) {
            return Result.err(
                mkMalformed("insertChildElement returned no contentHash"),
            );
        }
        return Result.ok({ contentHash, path: [block.id] });
    };
const insertBlock = insertBlockAt("last");
const insertFirstBlock = insertBlockAt("first");

// One manual "turn into" edit through the domain's `retype_element`
// primitive: `POST /commands/retypeElement`. `to` is the whole destination
// element the pure builders in Editor/edit.ts constructed; only its
// `type`/`text`/`attrs` travel over the wire (id-stability + children are
// the domain's to preserve — see `applyRetype` in @bilda/core). Reuses
// `toWireElement` so the same serialisation `insertBlock` relies on stays
// the one place a UI Element becomes a wire node.

const retypeElement = async (
    materialId: MaterialId,
    path: readonly string[],
    to: Element,
    expectedContentHash: string,
): PortResult<{ readonly contentHash: string }> => {
    const wire = toWireElement(to);
    const r = await command("retypeElement", {
        materialId,
        path: [...path],
        newType: wire.type,
        text: wire.text,
        attrs: wire.attrs,
        expectedContentHash,
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("retypeElement returned no contentHash"));
    }
    return Result.ok({ contentHash });
};

// Insert one block AFTER a given anchor, through the domain's sibling-
// relative primitive: `POST /commands/insertElement` with `position:
// "after"`. The twin of `insertBlock` (which appends via
// `insertChildElement` at the root's end) — this is the `/` slash-menu's
// "insert here" gesture, landing the new block right after the one the
// author was typing into.

const insertBlockAfter = async (
    materialId: MaterialId,
    anchorPath: readonly string[],
    block: Element,
    expectedContentHash: string,
): PortResult<{
    readonly contentHash: string;
    readonly path: readonly string[];
}> => {
    const r = await command("insertElement", {
        materialId,
        anchorPath: [...anchorPath],
        position: "after",
        element: toWireElement(block),
        expectedContentHash,
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("insertElement returned no contentHash"));
    }
    return Result.ok({ contentHash, path: [block.id] });
};

// Append one choice to an MCQ through the SAME primitive the agent uses:
// `POST /commands/insertChildElement` with the MCQ's element path as parent and
// `position: "last"`. The UI Choice is serialised to a domain `option` node by
// `toWireChoice` (text + `attrs.correct`). Returns the new content hash plus the
// choice's domain path (`[...mcqPath, choice.id]`) so the host makes it editable.

const addChoice = async (
    materialId: MaterialId,
    mcqPath: readonly string[],
    choice: Choice,
    expectedContentHash: string,
): PortResult<{
    readonly contentHash: string;
    readonly path: readonly string[];
}> => {
    const r = await command("insertChildElement", {
        materialId,
        parentPath: [...mcqPath],
        position: "last",
        element: toWireChoice(choice),
        expectedContentHash,
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(
            mkMalformed("insertChildElement returned no contentHash"),
        );
    }
    return Result.ok({ contentHash, path: [...mcqPath, choice.id] });
};

// Remove one element (a block OR a choice) through the SAME primitive the agent
// uses: `POST /commands/removeElement`. `path` is the element's id-chain.

const removeElement = async (
    materialId: MaterialId,
    path: readonly string[],
    expectedContentHash: string,
): PortResult<{ readonly contentHash: string }> => {
    const r = await command("removeElement", {
        materialId,
        path: [...path],
        expectedContentHash,
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("removeElement returned no contentHash"));
    }
    return Result.ok({ contentHash });
};

// Move one element (a block OR a choice) to a sibling slot through the SAME
// primitive the agent uses: `POST /commands/moveElement`. The host has already
// translated the reorder's target index into `targetAnchorPath` + `position`.

const moveElement = async (
    materialId: MaterialId,
    path: readonly string[],
    targetAnchorPath: readonly string[],
    position: "before" | "after",
    expectedContentHash: string,
): PortResult<{ readonly contentHash: string }> => {
    const r = await command("moveElement", {
        materialId,
        path: [...path],
        targetAnchorPath: [...targetAnchorPath],
        position,
        expectedContentHash,
    });
    if (Result.isErr(r)) return r;
    const contentHash = str(fields(r.value)?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("moveElement returned no contentHash"));
    }
    return Result.ok({ contentHash });
};

export const materialEditingHttp: MaterialEditingPorts = {
    updateElementText,
    retypeElement,
    insertBlockAfter,
    insertBlock,
    insertFirstBlock,
    updateElementAttrs,
    patchElementAttrs,
    addChoice,
    removeElement,
    moveElement,
};
